import React, { useCallback } from 'react';
import { User } from '../../../functions/src/types';
import { getValueFromLocalStorage, removeValueFromLocalStorage, saveValueToLocalStorage } from '../LocalStorage';
import { exchangeToken, refreshToken } from '../ApiClient';
import { verifyAndUnpack } from '../Cognito';
import { debuglog } from '../Log';

const FIVE_MINUTES = 5 * 60 * 1000;
const LOCAL_STORAGE_KEY = 'stashsite.user';

export const enum UserState {
  INITIAL = 'Initial', // initial state
  CHECKING_STORED_USER = 'CheckingStoredUser', // reading the user in local storage
  NO_REFRESH_TOKEN = 'NoRefreshToken', // no valid token stored, user needs to login
  CHECK_TOKEN = 'CheckToken', // need to check the token for validity and expiry
  CHECKING_TOKEN = 'CheckingToken', // calling the refresh token endpoint if required
  LOGGED_IN = 'LoggedIn' // ready to go
}

interface UserContextProps {
  user: User | null;
  userState: UserState;
  checkToken: () => Promise<User | null>;
  loginWithCode: (token: string) => Promise<User | null>;
  logout: () => void;
}

const UserContext = React.createContext<UserContextProps>({
  user: null,
  userState: UserState.INITIAL,
  checkToken: async () => { return null; },
  loginWithCode: async () => { return null; },
  logout: () => { }
});

export const useUserContext = (): UserContextProps =>
  React.useContext(UserContext);

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
}: UserProviderProps) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [userState, setUserState] = React.useState<UserState>(UserState.INITIAL);

  const checkToken = useCallback(async () => {
    if (user) {
      const now = new Date();
      // if we are within five minutes of expiry, attempt a refresh
      if ((user.exp * 1000) - now.getTime() < FIVE_MINUTES) {
        debuglog('UserProvider: token is about to expire or already has: refreshing token');
        const newTokenSet = await refreshToken(user.refresh_token);
        if (newTokenSet) {
      const unpackedToken = await verifyAndUnpack(newTokenSet.id_token, 'id');
      if (unpackedToken) {
        const refreshedUser = {
          ...user,
          access_token: newTokenSet.access_token,
          id_token: newTokenSet.id_token,
          refresh_token: newTokenSet.refresh_token,
          exp: unpackedToken.exp,
          picture: unpackedToken.picture as string | undefined,
        };
        setUser(refreshedUser);
        saveValueToLocalStorage(LOCAL_STORAGE_KEY, refreshedUser);
        return refreshedUser;
      }
          else {
            // shouldn't really happen, but you never know
            // clear the user and the local storage
            setUser(null);
            removeValueFromLocalStorage(LOCAL_STORAGE_KEY);
            return null;
          }
        }
        else {
          // clear the user and the local storage
          setUser(null);
          removeValueFromLocalStorage(LOCAL_STORAGE_KEY);
          return null;
        }

      }
      else {
        return user;
      }
    }
    else {
      return null;
    }
  }, [user]);

  React.useEffect(() => {
   debuglog(`UserProvider: userState: ${userState}`);
    if (userState === UserState.INITIAL) {
      setUserState(UserState.CHECKING_STORED_USER);
      const storedUser = getValueFromLocalStorage(LOCAL_STORAGE_KEY);
      if (storedUser) {
        setUser(storedUser);
        setUserState(UserState.CHECK_TOKEN);
      }
      else {
        setUserState(UserState.NO_REFRESH_TOKEN);
      }
    }
    else if (userState === UserState.CHECK_TOKEN) {
      setUserState(UserState.CHECKING_TOKEN);
      (async () => {
        const tokenOK = await checkToken();
        if (tokenOK) {
         debuglog('UserProvider: token OK');
          setUserState(UserState.LOGGED_IN);
        }
        else {
          setUserState(UserState.NO_REFRESH_TOKEN);
         debuglog('UserProvider: token not OK');
        }
      })();
    }
  }, [checkToken, userState]);

  const loginWithCode = useCallback(async (code: string) => {
    const newTokens = await exchangeToken(code);
    if (newTokens) {
      const unpackedToken = await verifyAndUnpack(newTokens.id_token, 'id');
      if (unpackedToken) {
        // Check if picture was added by backend (from Google userinfo endpoint)
        const picture = (unpackedToken.picture as string | undefined) || 
                       ((newTokens as any).picture as string | undefined);
        
        const unpackedUser = {
          uid: unpackedToken['cognito:username'] as string,
          email: unpackedToken.email as string,
          access_token: newTokens.access_token,
          id_token: newTokens.id_token,
          refresh_token: newTokens.refresh_token,
          exp: unpackedToken.exp,
          picture: picture,
        } as User;
        setUser(unpackedUser);
        saveValueToLocalStorage(LOCAL_STORAGE_KEY, unpackedUser);
        setUserState(UserState.LOGGED_IN);
        return unpackedUser;
      }
    }
    return null;
  }, [setUser]);

  const contextValue: UserContextProps = {
    user,
    userState,
    checkToken,
    loginWithCode,
    logout: () => {
      setUser(null);
      removeValueFromLocalStorage(LOCAL_STORAGE_KEY);
      setUserState(UserState.NO_REFRESH_TOKEN);
    }
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

